import type { MenuListItemVariantProps } from './MenuListItem.variants';

type Size = NonNullable<MenuListItemVariantProps['size']>;
type Variant = NonNullable<MenuListItemVariantProps['variant']>;

/**
 * Leading (Icon-Left) size per row size — exact from Figma's per-size nodes
 * (e.g. XL = `size-[26px]` in node 1284:12109). List-row icons run a touch
 * smaller than `Button`'s at the equivalent height because the rows are
 * denser.
 */
export const ICON_SIZE_FOR: Record<Size, number> = {
  xl: 26,
  l: 22,
  m: 20,
  s: 14,
};

/**
 * Trailing (Icon-Right) size per row size — exact from Figma's selected-state
 * nodes (`Icon-Right` is `size-[22px]` at XL/L, `size-[20px]` at M,
 * `size-[16px]` at S). It does NOT mirror the leading size at every band, so
 * it's its own map. Drives the auto-check and any explicit `rightIcon`.
 */
export const TRAILING_ICON_SIZE_FOR: Record<Size, number> = {
  xl: 22,
  l: 22,
  m: 20,
  s: 16,
};

const ICON_COLOR: Record<
  Variant,
  { default: { light: string; dark: string }; selected: { light: string; dark: string } }
> = {
  // Neutral grey, both states. text-secondary-600 (#525252) /
  //  (#a1a1a1).
  secondary: {
    default: { light: '#525252', dark: '#a1a1a1' },
    selected: { light: '#525252', dark: '#a1a1a1' },
  },
  // Grey at rest (matches the label — Primary is neutral until interacted
  // with), brand purple when selected. Bound to the primary-600 token
  // (Figma nodes 1000:8506 light / 3380:21049 dark): light #7f22fe /
  // dark #a684ff. Must equal the auto-inverting label colour
  // (`text-primary-600`) so the icon and label never diverge in dark mode.
  primary: {
    default: { light: '#525252', dark: '#a1a1a1' },
    selected: { light: '#7f22fe', dark: '#a684ff' },
  },
  // Red, both states. red-600 (#e7000b) / dark red-400 (#ff6467).
  error: {
    default: { light: '#e7000b', dark: '#ff6467' },
    selected: { light: '#e7000b', dark: '#ff6467' },
  },
};

/**
 * Resolve the icon hex for a (variant, selected, dark) tuple. Used on native,
 * where `react-native-svg` accepts `color` as a prop instead of a
 * `currentColor`-style className. On web the component passes `currentColor`
 * instead, so icons inherit the row's text colour (incl. the hover/pressed
 * purple shift that only exists on web).
 *
 * Disabled is NOT a separate path — disabled rows drop to `opacity-30`,
 * and the icon inherits that opacity via its parent.
 */
export function resolveMenuListItemIconColor(
  variant: Variant,
  selected: boolean,
  isDark: boolean,
): string {
  const pair = ICON_COLOR[variant][selected ? 'selected' : 'default'];
  return isDark ? pair.dark : pair.light;
}
